import { MessageSquareText, X } from "lucide-react";
import type { PositionedTextAnnotation } from "playground/annotations/annotations";
import { useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Every thread on the file, open first. Resolved threads are kept, behind a
 * toggle. A thread whose text is gone opens here, since it has nowhere to
 * anchor in the document.
 */
export function CommentsList({
  threads,
  activeId,
  unanchored,
  onSelect,
  onClose,
}: {
  threads: PositionedTextAnnotation[];
  activeId: string | null;
  unanchored: ReactNode;
  onSelect(thread: PositionedTextAnnotation): void;
  onClose(): void;
}) {
  const [showResolved, setShowResolved] = useState(false);
  const resolved = threads.filter((thread) => thread.annotation.resolved);
  const shown = threads.filter(
    (thread) => showResolved || !thread.annotation.resolved,
  );

  return (
    <aside className="absolute inset-y-0 right-0 z-20 flex w-[min(20rem,100%)] flex-col border-l bg-background shadow-lg">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b px-3 text-sm font-medium">
        <span className="flex-1">Comments</span>
        {resolved.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => setShowResolved((value) => !value)}
          >
            {showResolved
              ? "Hide resolved"
              : `Show resolved (${resolved.length})`}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close comments"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {shown.length === 0 ? (
          <p className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
            <MessageSquareText className="size-4" />
            Select text, then Comment.
          </p>
        ) : null}
        {shown.map((thread) => {
          const messages = Object.values(thread.annotation.messages);
          const first = messages.sort((a, b) => a.createdAt - b.createdAt)[0];
          return (
            <div key={thread.annotation.id} className="mb-1">
              <button
                className={cn(
                  "w-full rounded-md px-2.5 py-2 text-left hover:bg-accent/50",
                  activeId === thread.annotation.id && "bg-accent/60",
                )}
                onClick={() => onSelect(thread)}
              >
                <p className="truncate border-l-2 border-amber-400 pl-2 text-xs text-muted-foreground">
                  {thread.anchor.quote}
                </p>
                {first ? (
                  <p className="mt-1 line-clamp-2 text-sm">{first.body}</p>
                ) : null}
                <p className="mt-1 flex gap-2 text-xs text-muted-foreground">
                  <span>
                    {messages.length === 1
                      ? "1 message"
                      : `${messages.length} messages`}
                  </span>
                  {thread.annotation.resolved ? <span>Resolved</span> : null}
                  {thread.range ? null : (
                    <span className="text-amber-700 dark:text-amber-400">
                      Text changed
                    </span>
                  )}
                </p>
              </button>
              {activeId === thread.annotation.id && unanchored ? (
                <div className="mt-1">{unanchored}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
